/**
 * SelectionFloatingToolbar.tsx — barre flottante contextuelle au-dessus de la sélection.
 * Inspirée du standalone Snapbooth Redesign : Playfair + size + color + B/I/U + align.
 *
 * Positionnée en absolu dans la zone canvas, suit la forme sélectionnée.
 * Affiche des contrôles différents selon le type (text / image / shape).
 */
import React, { useMemo } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Copy,
  Trash2,
  Lock,
  Crop,
  Plus,
  Minus,
  BringToFront,
  SendToBack,
} from 'lucide-react';
import { useAppStore } from '../../../services/StateService';
import type { ShapeData } from '../../../types/ShapeTypes';

const FONTS = [
  'Plus Jakarta Sans',
  'Playfair Display',
  'Inter',
  'Roboto',
  'Montserrat',
  'Lora',
  'Poppins',
  'Bebas Neue',
  'Caveat',
  'Dancing Script',
];

interface Props {
  shape: ShapeData;
  /** Position absolue dans le conteneur canvas (en px). */
  x: number;
  y: number;
}

export const SelectionFloatingToolbar: React.FC<Props> = ({ shape, x, y }) => {
  const updateShape = useAppStore(state => state.updateShape);
  const deleteShape = useAppStore(state => state.deleteShape);
  const bringToFront = useAppStore(state => state.bringToFront);
  const sendToBack = useAppStore(state => state.sendToBack);

  const isText = shape.type === 'text';
  const isImage = shape.type === 'image';
  // Formes géométriques natives → recolorables via `fill`.
  const isFillable = ['rect', 'circle', 'triangle', 'star', 'heart'].includes(shape.type);

  // Couleur normalisée
  const fill = useMemo(
    () => (typeof shape.fill === 'string' ? shape.fill : '#1A1530'),
    [shape.fill]
  );
  const fontSize = typeof shape.fontSize === 'number' ? shape.fontSize : 24;

  const set = (patch: Partial<ShapeData>) => updateShape(shape.id, patch);

  // Contrôle de couleur fonctionnel (input natif masqué sous la pastille).
  const colorHex = /^#([0-9a-f]{6})$/i.test(fill) ? fill : '#000000';
  const ColorControl = (
    <label
      className="sb-fl-btn"
      title={isText ? 'Couleur du texte' : 'Couleur de remplissage'}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <span className="sb-fl-swatch" style={{ background: fill }} />
      <input
        type="color"
        value={colorHex}
        onChange={e => set({ fill: e.target.value })}
        aria-label="Couleur"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          border: 'none',
          padding: 0,
        }}
      />
    </label>
  );

  return (
    <div
      className="sb-floating-toolbar"
      role="toolbar"
      aria-label="Outils contextuels de la sélection"
      style={{ left: x, top: y, transform: 'translate(-50%, -100%)' }}
    >
      {isText && (
        <>
          <select
            value={(shape.fontFamily as string) || FONTS[0]}
            onChange={e => set({ fontFamily: e.target.value })}
            aria-label="Police"
          >
            {FONTS.map(f => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>

          <div className="sb-fl-divider" />

          <div className="sb-fl-stepper" aria-label="Taille de police">
            <button
              onClick={() => set({ fontSize: Math.max(6, fontSize - 1) })}
              aria-label="Diminuer"
            >
              <Minus size={12} />
            </button>
            <span className="sb-fl-stepper-val">{fontSize}</span>
            <button
              onClick={() => set({ fontSize: Math.min(400, fontSize + 1) })}
              aria-label="Augmenter"
            >
              <Plus size={12} />
            </button>
          </div>

          <div className="sb-fl-divider" />

          {ColorControl}

          <div className="sb-fl-divider" />

          <button
            className={`sb-fl-btn ${shape.fontStyle?.includes('bold') ? 'is-active' : ''}`}
            onClick={() => {
              const wasBold = shape.fontStyle?.includes('bold') ?? false;
              const wasItalic = shape.fontStyle?.includes('italic') ?? false;
              const parts: string[] = [];
              if (!wasBold) parts.push('bold');
              if (wasItalic) parts.push('italic');
              set({ fontStyle: parts.join(' ') || 'normal' });
            }}
            aria-label="Gras"
            title="Gras (⌘B)"
          >
            <Bold size={14} />
          </button>
          <button
            className={`sb-fl-btn ${shape.fontStyle?.includes('italic') ? 'is-active' : ''}`}
            onClick={() => {
              const wasBold = shape.fontStyle?.includes('bold') ?? false;
              const wasItalic = shape.fontStyle?.includes('italic') ?? false;
              const parts: string[] = [];
              if (wasBold) parts.push('bold');
              if (!wasItalic) parts.push('italic');
              set({ fontStyle: parts.join(' ') || 'normal' });
            }}
            aria-label="Italique"
            title="Italique (⌘I)"
          >
            <Italic size={14} />
          </button>
          <button
            className={`sb-fl-btn ${shape.textDecoration === 'underline' ? 'is-active' : ''}`}
            onClick={() =>
              set({ textDecoration: shape.textDecoration === 'underline' ? 'none' : 'underline' })
            }
            aria-label="Souligné"
            title="Souligné (⌘U)"
          >
            <Underline size={14} />
          </button>
          <button
            className={`sb-fl-btn ${shape.textDecoration === 'line-through' ? 'is-active' : ''}`}
            onClick={() =>
              set({
                textDecoration: shape.textDecoration === 'line-through' ? 'none' : 'line-through',
              })
            }
            aria-label="Barré"
          >
            <Strikethrough size={14} />
          </button>

          <div className="sb-fl-divider" />

          {(['left', 'center', 'right', 'justify'] as const).map(a => {
            const Ico = {
              left: AlignLeft,
              center: AlignCenter,
              right: AlignRight,
              justify: AlignJustify,
            }[a];
            return (
              <button
                key={a}
                className={`sb-fl-btn ${shape.textAlign === a ? 'is-active' : ''}`}
                onClick={() => set({ textAlign: a })}
                aria-label={`Aligner ${a}`}
              >
                <Ico size={14} />
              </button>
            );
          })}

          <div className="sb-fl-divider" />
        </>
      )}

      {isFillable && (
        <>
          {ColorControl}
          <div className="sb-fl-divider" />
        </>
      )}

      {isImage && (
        <>
          <button
            className="sb-fl-btn"
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent('snapbooth:edit-crop', { detail: { id: shape.id } })
              )
            }
            aria-label="Rogner"
            title="Rogner l'image"
          >
            <Crop size={14} />
          </button>
          <div className="sb-fl-divider" />
        </>
      )}

      {/* Ordre des calques (z-order) — M6 : étaient inertes, désormais câblés. */}
      <button
        className="sb-fl-btn"
        onClick={() => bringToFront(shape.id)}
        aria-label="Mettre au premier plan"
        title="Premier plan (])"
      >
        <BringToFront size={14} />
      </button>
      <button
        className="sb-fl-btn"
        onClick={() => sendToBack(shape.id)}
        aria-label="Mettre à l'arrière-plan"
        title="Arrière-plan ([)"
      >
        <SendToBack size={14} />
      </button>
      <div className="sb-fl-divider" />

      {/* Actions communes à tout type de shape */}
      <button
        className="sb-fl-btn"
        onClick={() => set({ locked: !shape.locked })}
        aria-label={shape.locked ? 'Déverrouiller' : 'Verrouiller'}
        title="Verrouiller (⇧L)"
      >
        <Lock size={14} />
      </button>
      <button
        className="sb-fl-btn"
        onClick={() => {
          // Duplication simple : recréer une shape légèrement décalée
          const dup: ShapeData = {
            ...shape,
            id: `${shape.id}-copy-${Date.now()}`,
            x: shape.x + 20,
            y: shape.y + 20,
          };
          useAppStore.getState().addShape(dup);
        }}
        aria-label="Dupliquer"
        title="Dupliquer (⌘D)"
      >
        <Copy size={14} />
      </button>
      <button
        className="sb-fl-btn"
        onClick={() => deleteShape(shape.id)}
        aria-label="Supprimer"
        title="Supprimer (⌫)"
        style={{ color: 'var(--danger)' }}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};
